import { getContrastRatio, lighten } from '@mui/material/styles';
import { tintOnWhite } from '@/theme/colorUtils';
import { getUiTokens, uiTokens, type UiTone } from '@/theme/uiTokens';
import { darken } from '@/ui/primitives/colorUtils';
import type { StatusTone } from '@/ui/primitives/Chip';
import type { PaletteMode, SxProps, Theme } from '@/ui/primitives/types';
import type {
  BuildStatus,
  CiConclusion,
  CiLifecycleState,
  ExecutionStatusKey,
  FeatureStatus,
  ReleaseStatus,
  SuiteRunStatus,
  TestRunStatus,
  UploadStatus,
} from '@flowqa/shared';

export type StatusChipStatus =
  | BuildStatus
  | CiLifecycleState
  | CiConclusion
  | ExecutionStatusKey
  | FeatureStatus
  | ReleaseStatus
  | SuiteRunStatus
  | TestRunStatus
  | UploadStatus
  | 'untested';

export type ExecutionChipStatus = ExecutionStatusKey | SuiteRunStatus | TestRunStatus;

export type StatusChipColor = UiTone;

export const STATUS_COLOR_MAP: Record<StatusChipStatus, StatusChipColor> = {
  draft: 'neutral',
  planned: 'blue',
  overdue: 'red',
  ready: 'orange',
  released: 'green',
  queued: 'neutral',
  waiting: 'orange',
  processing: 'blue',
  running: 'blue',
  completed: 'green',
  completed_with_errors: 'brown',
  success: 'green',
  warning: 'orange',
  canceled: 'red',
  neutral: 'neutral',
  action_required: 'orange',
  timed_out: 'red',
  unknown: 'neutral',
  passed: 'green',
  failed: 'red',
  errored: 'brown',
  skipped: 'orange',
  untested: 'neutral',
};

export const STATUS_LABEL_MAP: Record<StatusChipStatus, string> = {
  draft: 'Drafted',
  planned: 'Planned',
  overdue: 'Overdue',
  ready: 'Ready',
  released: 'Released',
  queued: 'Queued',
  waiting: 'Waiting',
  processing: 'Processing',
  running: 'Running',
  completed: 'Completed',
  completed_with_errors: 'Completed with errors',
  success: 'Success',
  warning: 'Warning',
  canceled: 'Canceled',
  neutral: 'Neutral',
  action_required: 'Action Required',
  timed_out: 'Timed Out',
  unknown: 'Unknown',
  passed: 'Passed',
  failed: 'Failed',
  errored: 'Errored',
  skipped: 'Skipped',
  untested: 'Untested',
};

export const getStatusChipColor = (status?: StatusChipStatus | null): StatusChipColor => {
  if (!status) return 'neutral';
  return status in STATUS_COLOR_MAP ? STATUS_COLOR_MAP[status] : 'neutral';
};

export const formatStatusLabel = (status?: StatusChipStatus | null): string => {
  if (!status) return '';
  return STATUS_LABEL_MAP[status];
};

export const resolveStatusChipTone = (status?: StatusChipStatus | null): StatusTone =>
  getStatusChipColor(status);

const getContrastSafeToneColor = (theme: Theme, baseColor: string): string => {
  const backgrounds = [theme.palette.background.default, theme.palette.background.paper].filter(
    (value, index, list) => list.indexOf(value) === index,
  );
  const meetsContrast = (color: string): boolean =>
    backgrounds.every((background) => getContrastRatio(color, background) >= 4.5);
  if (meetsContrast(baseColor)) return baseColor;
  const adjust = theme.palette.mode === 'dark' ? lighten : darken;
  const steps =
    theme.palette.mode === 'dark' ? [0.2, 0.3, 0.4, 0.5, 0.6] : [0.1, 0.2, 0.3, 0.4, 0.5];
  for (const step of steps) {
    const candidate = adjust(baseColor, step);
    if (meetsContrast(candidate)) return candidate;
  }
  return baseColor;
};

const getToneBaseColor = (theme: Theme, tone: StatusChipColor): string => {
  const isDarkMode = theme.palette.mode === 'dark';
  if (tone === 'green') {
    return isDarkMode ? theme.palette.success.light : theme.palette.success.dark;
  }
  if (tone === 'red') {
    return isDarkMode ? theme.palette.error.light : theme.palette.error.dark;
  }
  if (tone === 'orange') {
    return isDarkMode ? theme.palette.warning.light : theme.palette.warning.dark;
  }
  if (tone === 'blue') {
    return isDarkMode ? theme.palette.info.light : theme.palette.info.dark;
  }
  if (tone === 'purple') {
    return isDarkMode ? theme.palette.secondary.light : theme.palette.secondary.dark;
  }
  if (tone === 'teal') {
    return isDarkMode ? theme.palette.primary.light : theme.palette.primary.dark;
  }
  if (tone === 'brown') {
    return theme.palette.brown.main;
  }
  return theme.palette.neutral.main;
};

export const getStatusToneTextColor = (theme: Theme, tone: StatusChipColor): string =>
  getContrastSafeToneColor(theme, getToneBaseColor(theme, tone));

export const getStatusTextColor = (theme: Theme, status?: StatusChipStatus | null): string =>
  getStatusToneTextColor(theme, getStatusChipColor(status));

export const resolveStatusChipLabel = (
  status?: StatusChipStatus | null,
  emptyLabel = '',
): string => (status ? formatStatusLabel(status) : emptyLabel);

const resolveStatusPalette = (theme: Theme, color: StatusChipColor): { main: string } => {
  return { main: getToneBaseColor(theme, color) };
};

type StatusChipStyle = { background: string; border: string; text: string; icon: string };

export type ChipSx = SxProps<Theme>;

export type ChipColorKey =
  | 'grey'
  | 'green'
  | 'red'
  | 'yellow'
  | 'brown'
  | 'blue'
  | 'purple'
  | 'pink'
  | 'teal';

type ChipStyleConfig = {
  backgroundAlpha: number;
  borderAlpha: number;
  textDarken: number;
  iconDarken: number;
};

const CHIP_STYLE_CONFIG = {
  light: {
    backgroundAlpha: 0.3,
    borderAlpha: 1,
    textDarken: 0.6,
    iconDarken: 1,
  },
  dark: {
    backgroundAlpha: 0.65,
    borderAlpha: 1,
    textDarken: 1,
    iconDarken: 1,
  },
} satisfies Record<'light' | 'dark', ChipStyleConfig>;

const buildChipSx = (style: StatusChipStyle): ChipSx => ({
  backgroundColor: style.background,
  color: style.text,
  border: 'none',
  '& .MuiChip-label': { color: style.text },
  '& .MuiChip-icon': { color: style.icon },
});

const resolveChipStyleConfig = (mode: PaletteMode): ChipStyleConfig =>
  mode === 'dark' ? CHIP_STYLE_CONFIG.dark : CHIP_STYLE_CONFIG.light;

const buildStyleFromBaseColor = (
  base: string,
  config: ChipStyleConfig,
  mode: PaletteMode,
): StatusChipStyle => {
  const background =
    mode === 'dark'
      ? darken(base, 1 - config.backgroundAlpha)
      : tintOnWhite(base, config.backgroundAlpha);
  const border =
    mode === 'dark' ? darken(base, 1 - config.borderAlpha) : tintOnWhite(base, config.borderAlpha);
  return {
    background,
    border,
    text: darken(base, config.textDarken),
    icon: darken(base, config.iconDarken),
  };
};

const buildStatusChipColors = (
  tokens: ReturnType<typeof getUiTokens>,
  config: ChipStyleConfig,
  mode: PaletteMode,
): Record<ChipColorKey, StatusChipStyle> => {
  const neutralBackgroundAlpha =
    mode === 'dark' ? Math.max(0, config.backgroundAlpha - 0.08) : config.backgroundAlpha;
  return {
    grey: {
      background:
        mode === 'dark'
          ? darken(tokens.statusColors.neutral, 1 - neutralBackgroundAlpha)
          : tintOnWhite(tokens.statusColors.neutral, neutralBackgroundAlpha),
      border:
        mode === 'dark'
          ? darken(tokens.statusColors.neutral, 1 - config.borderAlpha)
          : tintOnWhite(tokens.statusColors.neutral, config.borderAlpha),
      text: darken(tokens.statusColors.neutral, config.textDarken),
      icon: darken(tokens.statusColors.neutral, config.iconDarken),
    },
    green: { ...buildStyleFromBaseColor(tokens.statusColors.green, config, mode) },
    red: { ...buildStyleFromBaseColor(tokens.statusColors.red, config, mode) },
    yellow: { ...buildStyleFromBaseColor(tokens.statusColors.orange, config, mode) },
    brown: { ...buildStyleFromBaseColor(tokens.statusColors.brown, config, mode) },
    blue: { ...buildStyleFromBaseColor(tokens.statusColors.blue, config, mode) },
    purple: { ...buildStyleFromBaseColor(tokens.statusColors.purple, config, mode) },
    pink: { ...buildStyleFromBaseColor(tokens.statusColors.pink, config, mode) },
    teal: { ...buildStyleFromBaseColor(tokens.statusColors.teal, config, mode) },
  };
};

const STATUS_CHIP_COLORS: Record<ChipColorKey, StatusChipStyle> = buildStatusChipColors(
  uiTokens,
  CHIP_STYLE_CONFIG.light,
  'light',
);

const STATUS_TO_CHIP_COLOR: Record<StatusChipStatus, ChipColorKey | undefined> = {
  draft: 'grey',
  planned: 'blue',
  overdue: 'red',
  ready: 'yellow',
  released: 'green',
  queued: 'grey',
  waiting: 'yellow',
  processing: 'blue',
  running: 'blue',
  completed: 'green',
  completed_with_errors: 'brown',
  success: 'green',
  warning: 'yellow',
  canceled: 'red',
  neutral: 'grey',
  action_required: 'yellow',
  timed_out: 'red',
  unknown: 'grey',
  passed: 'green',
  failed: 'red',
  errored: 'brown',
  skipped: 'yellow',
  untested: 'grey',
};

export function normaliseSummaryChipStatus(
  status?: ExecutionChipStatus | null,
): ExecutionChipStatus | null;
export function normaliseSummaryChipStatus(
  status?: StatusChipStatus | null,
): StatusChipStatus | null;
export function normaliseSummaryChipStatus(
  status?: StatusChipStatus | null,
): StatusChipStatus | null {
  if (!status) return null;
  if (status === 'queued' || status === 'waiting' || status === 'running') {
    return 'skipped';
  }
  return status;
}

export const getStatusChipKey = (status?: StatusChipStatus | null): ChipColorKey => {
  const key = status ? STATUS_TO_CHIP_COLOR[status] : undefined;
  return key ?? 'grey';
};

export const getChipPalette = (key: ChipColorKey): StatusChipStyle => STATUS_CHIP_COLORS[key];

export const getStatusChipSxFromColor = (theme: Theme, color: StatusChipColor): ChipSx => {
  const palette = resolveStatusPalette(theme, color);
  return buildChipSx(
    buildStyleFromBaseColor(
      palette.main,
      resolveChipStyleConfig(theme.palette.mode),
      theme.palette.mode,
    ),
  );
};

export const getChipSx = (key: ChipColorKey): ChipSx => buildChipSx(getChipPalette(key));

export const getChipSxForMode = (mode: PaletteMode, key: ChipColorKey): ChipSx => {
  const tokens = getUiTokens(mode === 'dark' ? 'dark' : 'light');
  const config = resolveChipStyleConfig(mode);
  return buildChipSx(buildStatusChipColors(tokens, config, mode)[key]);
};

export const getChipPaletteForMode = (mode: PaletteMode, key: ChipColorKey): StatusChipStyle => {
  const tokens = getUiTokens(mode === 'dark' ? 'dark' : 'light');
  const config = resolveChipStyleConfig(mode);
  return buildStatusChipColors(tokens, config, mode)[key];
};

export const mergeChipSx = (base: ChipSx, overrides?: ChipSx): ChipSx => {
  if (!overrides) return base;
  const baseObject = base as Record<string, SxProps<Theme>>;
  const overrideObject = overrides as Record<string, SxProps<Theme>>;
  const isRecord = (value: SxProps<Theme> | undefined): value is Record<string, SxProps<Theme>> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
  const baseLabel = isRecord(baseObject['& .MuiChip-label']) ? baseObject['& .MuiChip-label'] : {};
  const overrideLabel = isRecord(overrideObject['& .MuiChip-label'])
    ? overrideObject['& .MuiChip-label']
    : {};
  const baseIcon = isRecord(baseObject['& .MuiChip-icon']) ? baseObject['& .MuiChip-icon'] : {};
  const overrideIcon = isRecord(overrideObject['& .MuiChip-icon'])
    ? overrideObject['& .MuiChip-icon']
    : {};

  return {
    ...baseObject,
    ...overrideObject,
    '& .MuiChip-label': { ...baseLabel, ...overrideLabel },
    '& .MuiChip-icon': { ...baseIcon, ...overrideIcon },
  };
};
